import React, { useState } from "react";
import axios from "axios";
import { Formik, Form } from "formik";
import SubmitButton from "../../components/Forms/SubmitButton";
import SettingsTextInput from "../../components/Forms/SettingsTextInput";
import SettingsSelect from "../../components/Forms/SettingsSelect";
import moment from "moment";

export default function ReportChatAudit() {
    const [loading, setLoading] = useState(false);
    const [auditLogs, setAuditLogs] = useState([]);
    const [showMessages, setShowMessages] = useState(false);
    const [expandedMessages, setExpandedMessages] = useState({});

    const toggleMessageExpansion = (index) => {
        setExpandedMessages(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const truncateMessage = (message, maxLength = 100) => {
        if (!message || message.length <= maxLength) return message;
        return message.substring(0, maxLength) + '...';
    };

    const getPlatformBadge = (platform) => {
        const badges = {
            whatsapp: { color: 'bg-green-500', label: 'WhatsApp' },
            email: { color: 'bg-blue-500', label: 'Email' },
            sms: { color: 'bg-orange-500', label: 'SMS' },
            audit_log: { color: 'bg-gray-500', label: 'Audit' }
        };
        const badge = badges[platform] || badges.audit_log;
        return (
            <span className={`${badge.color} text-white text-xs px-2 py-1 rounded`}>
                {badge.label}
            </span>
        );
    };

    const getDirectionBadge = (direction) => {
        if (direction === 'incoming') {
            return <span className="text-xs text-blue-600">← Incoming</span>;
        } else if (direction === 'outgoing') {
            return <span className="text-xs text-green-600">→ Outgoing</span>;
        }
        return null;
    };

    return (
        <>
            <div className="w-auto bg-gray-100 my-6 xl:my-0 xl:mx-4 rounded-3xl flex-1 p-5">
                <h1>Chat Audit</h1>

                <Formik
                    initialValues={{
                        booking_reference: "",
                        audit_type: "chat",
                    }}
                    onSubmit={async (values, { setSubmitting, setErrors, resetForm }) => {
                        const requestData = { 
                            booking_reference: values.booking_reference, 
                            audit_type: values.audit_type,
                            include_messages: showMessages
                        };
                        
                        console.log('Chat Audit Request:', requestData);
                        
                        await axios({
                            method: "post",
                            url: `${process.env.REACT_APP_API_URL}reports/get_audit_log`,
                            data: requestData,
                        })
                            .then((response) => {
                                console.log('Chat Audit Response:', {
                                    total_items: response.data.length,
                                    message_count: response.data.filter(item => item.type === 'message').length,
                                    audit_count: response.data.filter(item => item.type === 'audit_log').length,
                                    sample: response.data.slice(0, 3)
                                });
                                setAuditLogs(response.data);
                            })
                            .catch((error) => {
                                console.log('Chat Audit Error:', error);
                            });

                        setSubmitting(false);
                    }}
                >
                    {({ values, errors, setFieldValue, handleSubmit, isValid, isSubmitting, dirty }) => (
                        <Form onSubmit={handleSubmit}>
                            <div>
                                <div className="grid md:lg:grid-cols-3 gap-12 mt-5 mb-5 ">
                                    <div>
                                        <SettingsSelect
                                            label="What sort of report would you like to run?"
                                            name="audit_type"
                                            onChange={setFieldValue}
                                            value={values.audit_type}
                                            error={errors.audit_type}
                                            options={[
                                                { value: "chat", label: "Chat Audit" },
                                                { value: "user", label: "User Audit" },
                                            ]}
                                        />
                                    </div>
                                    <div>
                                        <SettingsTextInput
                                            label="Booking Reference"
                                            name="booking_reference"
                                            placeholder="Booking Reference"
                                            errors={errors.booking_reference}
                                            value={values.booking_reference}
                                        />
                                    </div>
                                </div>
                                
                                {values.audit_type === "chat" && (
                                    <div className="mb-4">
                                        <label className="flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={showMessages}
                                                onChange={(e) => setShowMessages(e.target.checked)}
                                                className="mr-2 h-4 w-4"
                                            />
                                            <span className="text-sm font-medium">Show Message History (WhatsApp, Email, SMS)</span>
                                        </label>
                                    </div>
                                )}
                                
                                <SubmitButton name="Submit" disabled={!isValid || isSubmitting || !dirty} isSubmitting={isSubmitting} text="View Audit" />
                            </div>
                        </Form>
                    )}
                </Formik>

                <div className="mt-5">
                    <table className="w-full">
                        <thead>
                            <tr>
                                {showMessages && (
                                    <th width="100" className="text-left">
                                        Type
                                    </th>
                                )}
                                <th width="200" className="text-left">
                                    Date
                                </th>
                                <th width="200" className="text-left">
                                    Romi User
                                </th>
                                <th width="200" className="text-left">
                                    Chat User
                                </th>
                                <th className="text-left">Action / Message</th>
                            </tr>
                        </thead>
                        <tbody>
                            {auditLogs?.map((log, index) => (
                                <tr 
                                    key={index} 
                                    className={`${
                                        log.type === 'message' 
                                            ? 'bg-white border-l-4 border-' + (log.platform === 'whatsapp' ? 'green' : log.platform === 'email' ? 'blue' : 'orange') + '-500' 
                                            : 'bg-gray-50'
                                    }`}
                                >
                                    {showMessages && (
                                        <td className="py-2">
                                            {getPlatformBadge(log.type === 'message' ? log.platform : 'audit_log')}
                                        </td>
                                    )}
                                    <td className="py-2">{moment(log.created_at).format("YYYY-MM-DD HH:mm:ss")}</td>
                                    <td className="py-2">{log.created_by}</td>
                                    <td className="py-2">{log.chat_by}</td>
                                    <td className="py-2">
                                        {log.type === 'message' ? (
                                            <div>
                                                <div className="mb-1">
                                                    {getDirectionBadge(log.direction)}
                                                    {log.to_name && (
                                                        <span className="text-xs text-gray-600 ml-2">
                                                            To: {log.to_name}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-sm">
                                                    {expandedMessages[index] || !log.message || log.message.length <= 100 ? (
                                                        log.message
                                                    ) : (
                                                        <>
                                                            {truncateMessage(log.message)}
                                                            <button
                                                                onClick={() => toggleMessageExpansion(index)}
                                                                className="text-blue-500 hover:text-blue-700 ml-2 text-xs"
                                                            >
                                                                Show more
                                                            </button>
                                                        </>
                                                    )}
                                                    {expandedMessages[index] && log.message && log.message.length > 100 && (
                                                        <button
                                                            onClick={() => toggleMessageExpansion(index)}
                                                            className="text-blue-500 hover:text-blue-700 ml-2 text-xs"
                                                        >
                                                            Show less
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                {log.value_old ? (
                                                    <>
                                                        {log.key} changed from <strong>{log.value_old}</strong> to <strong>{log.value}</strong>
                                                    </>
                                                ) : (
                                                    <>
                                                        {log.key} set as <strong>{log.value}</strong>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
}
